'use client';
import {
  DrawerBackHeader,
  DrawerBody,
  DrawerFooter,
  AssociatedDealerPriceWarning,
  AssociatedDealerDropdown,
  AssociatedDealerForm,
  Conditional,
  DrawerHeader
} from 'components/pcc-components';
import {
  DEALER_DROPDOWN_NAME,
  CUSTOMER_NUMBER_DROPDOWN_NAME,
  SELECT_CUSTOMER_FORM_ID,
  MO_STORE_LOCATION,
  END_USE_CODE_DROPDOWN_NAME,
  ORDER_TYPE_RADIO_NAME,
  TERMS_AND_CONDITIONS_CHECKBOX_NAME
} from '../../constants/constants';
import styles from './style.module.scss';
import { CatButton } from 'blocks-nextjs/blocks-react/components/Button';
import { useTranslations } from 'next-intl';
import { useDealerCustomer } from '../../hooks/use-dealer-customer';
import { FormProvider } from 'react-hook-form';
import { useMemo, useEffect, useState } from 'react';
import { TermsAndConditions } from '../terms-and-condtions';
import { OrderType } from '../order-type';
import {
  submitADP,
  getDealerCustomersByDCN
} from 'services/dealer-association/actions';
import { useAssociatedDealer } from 'components/associated-dealers/associated-dealer-context';
import {
  sortLabelName,
  getGaEvent,
  payloadBuilder,
  getSelectedDealer,
  getSelectedCustomerNumber
} from '../../utils';
import { useFormSubmitEvent } from 'hooks/analytics/use-form-submit-event';
import { STATUS_FAIL, STATUS_SUCCESS } from 'constants/analyticsConstants';
import { AssociatedDealerAddDealerButton } from '../associated-dealer-add-dealer-button';
import { DealerLocatorDrawerProvider } from 'components/dealer-locator/dealer-locator-drawer-context';
import { DealerLocatorDrawer } from 'components/dealer-locator';
import {
  AdpFormData,
  DropdownValue,
  FormType,
  StoreDropdownValue
} from 'components/associated-dealers/types';
import { DealerCustomer } from 'services/dealer-customer/types';
import { StoreAddress } from 'components/dealer-locator/types';
import { AssociatedDealerTitle } from '../associated-dealer-title';
import { replaceTokensInString } from '@cat-ecom/common/utils/stringUtils';
import { DealerResponse } from 'services/dealer';
import {
  DealerAssociationResponse,
  DealerErrorResponse
} from 'services/dealer-association';

interface SelectCustomerFormProps {
  dealerSearchForm: FormType;
  selectCustomerForm: FormType;
  isModal?: boolean;
  isDealerUser: boolean;
  isInstantAccessUser: boolean;
  isCspUser: boolean;
  isLoggedIn: boolean;
  dealerUserResponse?: DealerResponse;
  registeredUserResponse?: DealerAssociationResponse | DealerErrorResponse;
  storeAddress: StoreAddress;
  wrongSite?: boolean;
}

export const SelectCustomerForm = ({
  dealerSearchForm,
  selectCustomerForm,
  isModal = false,
  isDealerUser,
  isInstantAccessUser,
  isCspUser,
  isLoggedIn,
  dealerUserResponse = null,
  registeredUserResponse = null,
  storeAddress,
  wrongSite = false
}: SelectCustomerFormProps) => {
  const t = useTranslations();
  const { fireEvent } = useFormSubmitEvent();
  const [customerList, setCustomerList] = useState<DealerCustomer[] | null>([]);
  const [isLocationChangeFailed, setIsLocationChangeFailed] = useState(false);
  const [storeList, setStoreList] = useState<StoreDropdownValue[]>([]);
  const [dealerUserValue, setDealerUserValue] = useState<DropdownValue[]>([]);
  const [dealerUserCustomerName, setDealerUserCustomerName] = useState<
    DropdownValue[]
  >([]);
  const {
    showLoader,
    associatedDealerModal,
    associatedDealerDrawer,
    selectCustomerDrawer,
    searchResultsDrawer
  } = useAssociatedDealer();
  const {
    data,
    stores,
    startSearch,
    orderTypeInfo,
    isLoading,
    isRefetching,
    isError,
    remove
  } = useDealerCustomer();

  const dealerItemList = useMemo(
    () =>
      registeredUserResponse?.['associations']
        ?.map((association) => {
          return {
            label: association.dealerName,
            value: association.id,
            default: association.isDefault
          };
        })
        .sort(sortLabelName),
    [registeredUserResponse]
  );

  const currentDealerValue = selectCustomerForm.watch(DEALER_DROPDOWN_NAME);

  const selectedDealer = getSelectedDealer({
    currentDealerValue,
    associations: registeredUserResponse?.['associations']
  });

  const customerNumberList = useMemo(
    () =>
      selectedDealer?.customerNumbersInfo.map((association) => {
        return {
          label: association.name,
          value: association.value,
          default: association.isDefault
        };
      }),
    [selectedDealer?.customerNumbersInfo]
  );

  useEffect(() => {
    if (stores) {
      setStoreList(
        stores.map((store) => {
          return {
            endUseCodes: store.endUseCodes,
            id: store.id,
            default: store.isDefault,
            label: store.label,
            value: store.value
          };
        })
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading]);

  const currentStoreLocationValue = selectCustomerForm.watch(MO_STORE_LOCATION);
  const selectedStoreLocation = storeList?.find(
    (store) => store.label === currentStoreLocationValue
  );
  const endUseCodeList = selectedStoreLocation?.endUseCodes;
  const isSingleDealer = isDealerUser || dealerItemList?.length === 1;
  const isSingleCustomerNumber = customerNumberList?.length === 1;
  const isSingleStore = storeList?.length === 1;
  const isSingleEndUseCode = endUseCodeList?.length === 1;

  const showCustomerNumber = selectedDealer?.isCSP;
  const showEndUseCodeDropdown = endUseCodeList?.length > 0;
  const showOrderType = orderTypeInfo?.length > 1;
  const currentCustomerNumberValue = selectCustomerForm.watch(
    CUSTOMER_NUMBER_DROPDOWN_NAME
  );

  const currentOrderType = selectCustomerForm.watch(ORDER_TYPE_RADIO_NAME);
  const currentEndUseCode = selectCustomerForm.watch(
    END_USE_CODE_DROPDOWN_NAME
  );
  const currentTermsAndConditions = selectCustomerForm.watch(
    TERMS_AND_CONDITIONS_CHECKBOX_NAME
  );
  const selectedCustomerNumber = getSelectedCustomerNumber({
    customerNumberList,
    currentCustomerNumberValue
  });

  const isDisabledContinueButton =
    isError ||
    !currentDealerValue ||
    !currentCustomerNumberValue ||
    storeList?.length === 0 ||
    (storeList?.length > 0 && !currentStoreLocationValue) ||
    (orderTypeInfo?.length > 1 && !currentOrderType) ||
    (showEndUseCodeDropdown && !currentEndUseCode) ||
    !currentTermsAndConditions;

  useEffect(() => {
    if (isDealerUser) {
      setDealerUserValue([
        {
          label: dealerUserResponse.name,
          value: dealerUserResponse.storeId.toString(),
          default: true
        }
      ]);
      setDealerUserCustomerName([
        {
          label: `${data?.customerNumber}- ${data?.dealerCustomerBillingAddress.firstName}`,
          value: data?.customerNumber,
          default: true
        }
      ]);
    }
  }, [
    isDealerUser,
    data?.customerNumber,
    data?.dealerCustomerBillingAddress?.firstName,
    dealerUserResponse
  ]);

  useEffect(() => {
    if (selectedCustomerNumber?.value && selectedDealer?.id) {
      startSearch({
        customerNumber: selectedCustomerNumber.value,
        storeId: selectedDealer.id
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    currentCustomerNumberValue,
    selectedDealer?.id,
    selectedCustomerNumber?.value
  ]);

  //Submit
  const handleSubmit = () => {
    selectCustomerForm.handleSubmit((data: AdpFormData) => {
      const submitData = payloadBuilder({
        formData: data,
        selectedCustomerNumber,
        selectedStoreLocation,
        endUseCodeList,
        selectedDealer,
        dealerUserValue: dealerUserValue[0],
        dealerUserCustomerName: dealerUserCustomerName[0],
        isDealerUser
      });
      handlePayload(submitData);
    })();
  };

  const handlePayload = async (submitData) => {
    try {
      await submitADP(submitData);
      setIsLocationChangeFailed(false);
      showLoader(wrongSite);
      const successEvent = getGaEvent(false, false, STATUS_SUCCESS);
      fireEvent(successEvent);

      if (isModal && associatedDealerModal.modalControl.isActive) {
        associatedDealerModal.modalControl.close();
      } else {
        if (isDealerUser) {
          selectCustomerDrawer.closeDrawer();
          searchResultsDrawer.closeDrawer();
        }
        associatedDealerDrawer.closeDrawer();
      }
    } catch (error) {
      const errorEvent = getGaEvent(false, false, STATUS_FAIL);
      fireEvent(errorEvent);
      setIsLocationChangeFailed(true);
    }
  };

  const handleClose = () => {
    remove();
    selectCustomerDrawer.closeDrawer();
  };

  const handleClearSearch = () => {
    setIsLocationChangeFailed(false);
    dealerSearchForm.reset();
    searchResultsDrawer.closeDrawer();
    handleClose();
  };

  const cn = dealerUserCustomerName?.[0];

  const dealerCustomersByDCN = async (customerName) => {
    try {
      const response = await getDealerCustomersByDCN(customerName);
      setCustomerList(response.customers);
    } catch (error) {
      setCustomerList(null);
    }
  };
  useEffect(() => {
    if (isDealerUser && cn) {
      dealerCustomersByDCN(cn?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cn]);

  const getDealerListValues = () => {
    return isDealerUser ? dealerUserValue : dealerItemList;
  };

  const drawerTitle = (
    <AssociatedDealerTitle>
      {isDealerUser
        ? replaceTokensInString(
            t('GENERAL_BACK_TO', { 0: t('TITLE_SEARCH_RESULTS') })
          )
        : t('CAT_MA_ASSOCIATEDDEALERS')}
    </AssociatedDealerTitle>
  );

  return (
    <DealerLocatorDrawerProvider>
      <DealerLocatorDrawer
        autoSearch={!isCspUser && (!isLoggedIn || isInstantAccessUser)}
        isADP
        storeAddress={storeAddress}
      />
      <Conditional
        test={isDealerUser}
        fallback={<DrawerHeader>{drawerTitle}</DrawerHeader>}
      >
        <DrawerBackHeader handleClose={handleClose}>
          {drawerTitle}
        </DrawerBackHeader>
      </Conditional>
      <DrawerBody>
        <Conditional test={!isDealerUser}>
          <AssociatedDealerPriceWarning
            title={t('CAT_PRICE_MSG')}
            message={t('CAT_PROMO_MSG')}
          />
        </Conditional>
        <Conditional test={customerList === null}>
          <AssociatedDealerPriceWarning
            title={t('DEALER_USER_NO_REGISTERED_DCN')}
            message={t('DEALER_USER_ASSIST_DCN')}
          />
        </Conditional>
        <Conditional test={isLocationChangeFailed}>
          <AssociatedDealerPriceWarning
            title={t('TECH_ISSUE_CART_TITLE')}
            message={t('TECH_ISSUE_EMPTY_CART')}
          />
        </Conditional>
        <AssociatedDealerForm
          id={SELECT_CUSTOMER_FORM_ID}
          formMethods={selectCustomerForm}
          onSubmit={handleSubmit}
        >
          <div className="mt-3">
            <AssociatedDealerDropdown
              name={DEALER_DROPDOWN_NAME}
              placeholder={t('CAT_MA_SELECTDEALER')}
              label={t('Footer_Dealer')}
              itemList={getDealerListValues()}
              readOnly={isSingleDealer}
              required={true}
            />
          </div>
          <Conditional test={!isDealerUser}>
            <AssociatedDealerAddDealerButton />
          </Conditional>
          <div className="mt-3">
            <AssociatedDealerDropdown
              name={CUSTOMER_NUMBER_DROPDOWN_NAME}
              placeholder={t('CAT_MA_SELECTUCID')}
              label={t('CUST_NAME_NUMBER_COMBINED')}
              itemList={
                isDealerUser ? dealerUserCustomerName : customerNumberList
              }
              readOnly={
                isDealerUser || (isSingleCustomerNumber && showCustomerNumber)
              }
              showDropdown={showCustomerNumber}
              required={true}
            />
          </div>
          <div className="mt-3">
            <AssociatedDealerDropdown
              name={MO_STORE_LOCATION}
              placeholder={t('CAT_MA_SELECTDEALAERSTORE')}
              label={t('MO_STORE_LOCATION')}
              itemList={storeList}
              readOnly={isSingleStore}
              loading={isLoading || isRefetching}
              required={true}
              showDropdown={!isError}
            />
          </div>
          <Conditional test={showOrderType}>
            <div className="mt-3">
              <OrderType orderTypeInfo={orderTypeInfo} />
            </div>
          </Conditional>
          <div className="mt-3">
            <AssociatedDealerDropdown
              name={END_USE_CODE_DROPDOWN_NAME}
              placeholder={t('CAT_SHOPPING_PREFERENCES_SEL_END_USE_CODE')}
              label={t('CAT_SHOPPING_PREFERENCES_END_USE_CODE')}
              itemList={endUseCodeList}
              readOnly={isSingleEndUseCode}
              required={true}
              showDropdown={showEndUseCodeDropdown}
            />
          </div>
        </AssociatedDealerForm>
      </DrawerBody>
      <DrawerFooter>
        <FormProvider {...selectCustomerForm}>
          <TermsAndConditions
            selectedDealer={selectedDealer}
            dealerUserResponse={dealerUserResponse}
            isDealerUser={isDealerUser}
          />
        </FormProvider>
        <div className={styles['drawer-footer-buttons']}>
          <CatButton
            variant="primary"
            type="submit"
            disabled={isDisabledContinueButton}
            form={SELECT_CUSTOMER_FORM_ID}
          >
            {t('EF_CONTINUE')}
          </CatButton>
          <Conditional test={isDealerUser}>
            <CatButton variant="ghost" onClick={handleClearSearch}>
              {t('CLEAR_SEARCH')}
            </CatButton>
          </Conditional>
        </div>
      </DrawerFooter>
    </DealerLocatorDrawerProvider>
  );
};
